import type { WorkflowRunStepRunGraph } from '@/types/workflowRunStepRunGraph';
import { getIntegrationTypeForAppStoreType, getIsOTAPlatform } from '../helpers';
import { displayBetaServiceNameForAppStoreType } from '../helpers/displayHelpers';
import {
    AppPlatform,
    type AppStoreType,
    ReleaseStepType,
    type TimelineEventBatchActionRequestedData,
    TimelineEventType,
    TransactionType,
} from './../types';
import type { ReleaseStepStatus, TimelineEvent, User } from '../types';
import { buildStepConfigToDestinationsMap, collectStepsByArchetypeFromGraph } from './workflows';

export const generateTimelineEventsHistory: (args: {
    currentReleaseId?: string;
    version?: string;
    currentUser?: User;
    orgId?: string;
    appId?: string;
    appName?: string;
    platform?: AppPlatform;
    appStoreType?: AppStoreType;
    isReleaseComplete?: boolean;
}) => TimelineEvent[] = ({
    currentReleaseId,
    version,
    currentUser,
    orgId,
    appId,
    appName,
    platform,
    appStoreType,
    isReleaseComplete,
} = {}) => {
    const betaServiceName = appStoreType ? displayBetaServiceNameForAppStoreType(appStoreType) : '';
    const appStoreBuildIntegrationType = appStoreType ? getIntegrationTypeForAppStoreType(appStoreType) : '';

    const disallowedTimelineEventsForPlatform =
        getIsOTAPlatform(platform) ?
            [
                TimelineEventType.ReleaseSubmitted,
                TimelineEventType.ReleaseStepsReadyToSubmit,
                TimelineEventType.ReleaseTargetSubmissionDateUpdated,
                TimelineEventType.ReleaseStepMetadataApprovalChanged,
                TimelineEventType.ReleaseStepTranslationsApprovalChanged,
                TimelineEventType.ReleaseStepScreenshotAdded,
                TimelineEventType.ReleaseStepScreenshotDeleted,
                TimelineEventType.ReleaseStepScreenshotsApprovalChanged,
                TimelineEventType.RunwayEventReleaseStepAppPreviewAdded,
                TimelineEventType.RunwayEventReleaseStepAppPreviewDeleted,
                TimelineEventType.AppStoreVersionStatusUpdated,
                TimelineEventType.AppStoreVersionBetaSubmittedForReview,
                TimelineEventType.AppStoreVersionPhasedReleaseUpdated,
                TimelineEventType.AppStoreVersionReleaseNotesUpdated,
                TimelineEventType.AppStoreVersionAppStoreBuildChanged,
                TimelineEventType.ReleaseMarkedRejected,
                TimelineEventType.AndroidRolloutStartedManually,
                TimelineEventType.AndroidRolloutStartedFromForwardedEmail,
                TimelineEventType.BinaryArtifactAppStoreUploadSuccess,
                TimelineEventType.BinaryArtifactAppStoreUploadFailed,
                TimelineEventType.DsymArtifactStabilityMonitoringUploadSuccess,
                TimelineEventType.DsymArtifactStabilityMonitoringUploadFailed,
                TimelineEventType.ResignSequenceStarted,
                TimelineEventType.ResignSequenceCompleted,
                TimelineEventType.BuildResignStarted,
                TimelineEventType.BuildResignCompleted,
                TimelineEventType.SyncedVCSMetadata,
            ]
        :   [];

    const appStoreIntegration = platform === AppPlatform.Android ? 'google' : 'apple';
    const appStoreBuildNumber = platform === AppPlatform.Android ? '27500' : '298';
    const betaService = platform === AppPlatform.Android ? 'Google Play beta' : 'TestFlight';
    const ciBuildNumber = platform === AppPlatform.Android ? '275' : '298';

    const baseTimelineEvents = [
        {
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            appId: appId ?? 'demo',
            eventData: {
                appStoreBuildIntegrationType: appStoreIntegration,
                appStoreBuildNumber: appStoreBuildNumber,
                betaServiceName: betaService,
                ciBuildIntegrationType: 'github-ci',
                ciBuildNumber: ciBuildNumber,
                multipleBuildData: [
                    {
                        appStoreBuildNumber: appStoreBuildNumber,
                        ciBuildNumber: ciBuildNumber,
                    },
                ],
                regressionTestingStatus: 'passed',
            },
            orgId: orgId ?? 'demo',
            releaseData: {
                displayableVersion: version ?? '1.0.0',
                version: version ?? '1.0.0',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            status: 'completed',
            triggeredAt: generateRandomTimelineTimestamp(5, 12),
            triggeredBy: 'user',
            type: 'releaseStep.regressionTestingStatusChanged',
            userId: 'sully@demo.com',
            releaseStepArchetypes: [ReleaseStepType.RegressionTesting],
        },
        {
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            appId: appId ?? 'demo',
            eventData: null,
            orgId: orgId ?? 'demo',
            releaseData: {
                displayableVersion: version ?? '1.0.0',
                version: version ?? '1.0.0',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            status: 'completed',
            triggeredAt: generateRandomTimelineTimestamp(5, 12),
            triggeredBy: 'user',
            type: 'appStoreVersion.releaseNotesUpdated',
            userId: 'sully@demo.com',
            releaseStepArchetypes: [ReleaseStepType.Metadata],
        },
        {
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            appId: appId ?? 'demo',
            eventData: {
                approved: true,
            },
            orgId: orgId ?? 'demo',
            releaseData: {
                displayableVersion: version ?? '1.0.0',
                version: version ?? '1.0.0',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            status: 'completed',
            triggeredAt: generateRandomTimelineTimestamp(5, 12),
            triggeredBy: 'user',
            type: 'releaseStep.metadataApprovalChanged',
            userId: 'sully@demo.com',
            releaseStepArchetypes: [ReleaseStepType.Metadata],
        },
        {
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            appId: appId ?? 'demo',
            eventData: {
                appStoreBuildIntegrationType: appStoreIntegration,
                appStoreBuildNumber: appStoreBuildNumber,
                betaServiceName: betaService,
                multipleBuildData: [
                    {
                        appStoreBuildNumber: appStoreBuildNumber,
                        ciBuildNumber: ciBuildNumber,
                    },
                ],
            },
            orgId: orgId ?? 'demo',
            releaseData: {
                displayableVersion: version ?? '1.0.0',
                version: version ?? '1.0.0',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            status: 'completed',
            triggeredAt: generateRandomTimelineTimestamp(5, 13),
            triggeredBy: 'runway',
            type: 'appStoreVersion.appStoreBuildChanged',
            userId: null,
            releaseStepArchetypes: [ReleaseStepType.Submission],
        },
        {
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            appId: appId ?? 'demo',
            eventData: {
                artifactName: platform === AppPlatform.Android ? 'com.example.demo-release.aab' : 'demo.ipa',
                destination: betaService,
            },
            orgId: orgId ?? 'demo',
            releaseData: {
                displayableVersion: version ?? '1.0.0',
                version: version ?? '1.0.0',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            status: 'completed',
            triggeredAt: generateRandomTimelineTimestamp(5, 13),
            triggeredBy: 'runway',
            type: 'binaryArtifact.uploadSuccess',
            userId: null,
            releaseStepArchetypes: [ReleaseStepType.BetaTesting],
        },
        {
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            appId: appId ?? 'demo',
            eventData: {
                releaseStepStatus: 'ready',
                releaseStepType: 'releaseCandidate',
            },
            orgId: orgId ?? 'demo',
            releaseData: {
                displayableVersion: version ?? '1.0.0',
                version: version ?? '1.0.0',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            status: 'completed',
            triggeredAt: generateRandomTimelineTimestamp(5, 13),
            triggeredBy: 'unknown',
            type: 'releaseStep.statusChanged',
            userId: null,
            releaseStepArchetypes: [ReleaseStepType.ReleaseCandidate],
        },
        {
            type: 'releaseStep.betaSoakStarted',
            triggeredAt: generateRandomTimelineTimestamp(10, 10),
            triggeredBy: 'unknown',
            userId: null,
            status: 'completed',
            eventData: {
                appStoreBuildIntegrationType: appStoreBuildIntegrationType,
                appStoreBuildNumber: appStoreBuildNumber,
                betaServiceName: betaServiceName,
                ciBuildIntegrationType: 'bitrise',
                ciBuildNumber: ciBuildNumber,
                multipleBuildData: [],
            },
            orgId: orgId ?? 'demo',
            appId: appId ?? 'demo',
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            releaseData: {
                version: version ?? '1.0.0',
            },
            releaseStepArchetypes: [ReleaseStepType.BetaTesting],
        },
        {
            type: 'releaseStep.rcBuildStatusChanged',
            triggeredAt: generateRandomTimelineTimestamp(10, 10),
            triggeredBy: 'unknown',
            userId: null,
            status: 'completed',
            eventData: {
                buildStatus: 'success',
                ciBuildIntegrationType: 'bitrise',
                ciBuildNumber: ciBuildNumber,
            },
            orgId: orgId ?? 'demo',
            appId: appId ?? 'demo',
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            releaseData: {
                version: version ?? '1.0.0',
            },
            releaseStepArchetypes: [ReleaseStepType.ReleaseCandidate],
        },
        {
            type: 'releaseStep.statusChanged',
            triggeredAt: generateRandomTimelineTimestamp(17, 9),
            triggeredBy: 'unknown',
            userId: null,
            status: 'completed',
            eventData: {
                releaseStepStatus: 'pending',
                releaseStepType: 'betaTesting',
            },
            orgId: orgId ?? 'demo',
            appId: appId ?? 'demo',
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            releaseData: {
                version: version ?? '1.0.0',
            },
            releaseStepArchetypes: [ReleaseStepType.BetaTesting],
        },
        {
            type: 'releaseStep.statusChanged',
            triggeredAt: generateRandomTimelineTimestamp(11, 10),
            triggeredBy: 'unknown',
            userId: null,
            status: 'completed',
            eventData: {
                releaseStepStatus: 'pending',
                releaseStepType: 'regressionTesting',
            },
            orgId: orgId ?? 'demo',
            appId: appId ?? 'demo',
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            releaseData: {
                version: version ?? '1.0.0',
            },
            releaseStepArchetypes: [ReleaseStepType.RegressionTesting],
        },
        {
            type: 'releaseStep.rcBuildStatusChanged',
            triggeredAt: generateRandomTimelineTimestamp(17, 9),
            triggeredBy: 'unknown',
            userId: null,
            status: 'completed',
            eventData: {
                buildStatus: 'inProgress',
                ciBuildIntegrationType: 'bitrise',
                ciBuildNumber: '275',
            },
            orgId: orgId ?? 'demo',
            appId: appId ?? 'demo',
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            releaseData: {
                version: version ?? '1.0.0',
            },
            releaseStepArchetypes: [ReleaseStepType.ReleaseCandidate],
        },
        {
            type: 'release.branchCreated',
            triggeredAt: generateRandomTimelineTimestamp(17, 9),
            triggeredBy: 'runway',
            userId: null,
            status: 'completed',
            eventData: {
                releaseBranch: getIsOTAPlatform(platform) ? `codepush-${version}` : `release-${version}`,
            },
            orgId: orgId ?? 'demo',
            appId: appId ?? 'demo',
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            releaseData: {
                version: version ?? '1.0.0',
            },
            releaseStepArchetypes: [ReleaseStepType.Kickoff],
        },
        {
            type: 'release.targetKickoffDateUpdated',
            triggeredAt: generateRandomTimelineTimestamp(18, 8),
            triggeredBy: 'user',
            userId: currentUser?.email ?? 'mjemison@demo.com',
            status: 'completed',
            eventData: {
                targetKickoffDate: generateRandomTimelineTimestamp(17, 9),
            },
            orgId: orgId ?? 'demo',
            appId: appId ?? 'demo',
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            releaseData: {
                version: version ?? '1.0.0',
            },
            releaseStepArchetypes: [ReleaseStepType.Kickoff],
        },
        {
            type: 'release.targetKickoffDateUpdated',
            triggeredAt: generateRandomTimelineTimestamp(20, 6),
            triggeredBy: 'user',
            userId: 'mjemison@demo.com',
            status: 'completed',
            eventData: {
                targetKickoffDate: generateRandomTimelineTimestamp(17, 6),
            },
            orgId: orgId ?? 'demo',
            appId: appId ?? 'demo',
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            releaseData: {
                version: version ?? '1.0.0',
            },
            releaseStepArchetypes: [ReleaseStepType.Kickoff],
        },
        {
            type: 'release.versionBumped',
            triggeredAt: generateRandomTimelineTimestamp(33, 6),
            triggeredBy: 'runway',
            userId: null,
            status: 'completed',
            eventData: {
                bumpedVersion: version ?? '1.0.0',
                branch: 'develop',
            },
            orgId: orgId ?? 'demo',
            appId: appId ?? 'demo',
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            releaseData: {
                version: version ?? '1.0.0',
            },
            releaseStepArchetypes: [ReleaseStepType.Kickoff],
        },
        {
            type: 'release.created',
            triggeredAt: generateRandomTimelineTimestamp(33, 8),
            triggeredBy: 'runway',
            userId: null,
            status: 'completed',
            eventData: null,
            orgId: orgId ?? 'demo',
            appId: appId ?? 'demo',
            releaseId: currentReleaseId ?? '1.0.0',
            releaseData: {
                version: version ?? '1.0.0',
            },
            releaseStepArchetypes: [ReleaseStepType.Kickoff],
        },
    ] as TimelineEvent[];

    if (isReleaseComplete) {
        const submitAndReleaseTimelineEvents = [
            {
                appData: {
                    appName: appName ?? 'Demo',
                    platform: platform ?? 'ios',
                },
                appId: appId ?? 'demo',
                eventData: {
                    appStoreBuildIntegrationType: appStoreIntegration,
                    appStoreBuildNumber: appStoreBuildNumber,
                    betaServiceName: betaService,
                    dayNumber: 0,
                    multipleBuildData: [
                        {
                            appStoreBuildNumber: appStoreBuildNumber,
                            ciBuildNumber: ciBuildNumber,
                        },
                    ],
                    userFraction: 1,
                },
                orgId: orgId ?? 'demo',
                releaseData: {
                    displayableVersion: version ?? '1.0.0',
                    version: version ?? '1.0.0',
                },
                releaseId: currentReleaseId ?? '1.0.0',
                status: 'completed',
                triggeredAt: generateRandomTimelineTimestamp(0, 3),
                triggeredBy: 'runway',
                type: 'appStoreVersion.phasedReleaseUpdated',
                userId: null,
                releaseStepArchetypes: [ReleaseStepType.Takeoff, ReleaseStepType.Rollout],
            },
            {
                appData: {
                    appName: appName ?? 'Demo',
                    platform: platform ?? 'ios',
                },
                appId: appId ?? 'demo',
                eventData: {
                    vcsIntegration: 'github',
                },
                orgId: orgId ?? 'demo',
                releaseData: {
                    displayableVersion: version ?? '1.0.0',
                    version: version ?? '1.0.0',
                },
                releaseId: currentReleaseId ?? '1.0.0',
                status: 'completed',
                triggeredAt: generateRandomTimelineTimestamp(3, 3),
                triggeredBy: 'runway',
                type: 'release.tagged',
                userId: null,
                releaseStepArchetypes: [ReleaseStepType.Takeoff],
            },
            {
                appData: {
                    appName: appName ?? 'Demo',
                    platform: platform ?? 'ios',
                },
                appId: appId ?? 'demo',
                eventData: {
                    appStoreBuildIntegrationType: appStoreIntegration,
                    appStoreBuildNumber: appStoreBuildNumber,
                    betaServiceName: betaService,
                    multipleBuildData: [
                        {
                            appStoreBuildNumber: appStoreBuildNumber,
                            ciBuildNumber: ciBuildNumber,
                        },
                    ],
                    reviewSubmissionItems: [],
                },
                orgId: orgId ?? 'demo',
                releaseData: {
                    displayableVersion: version ?? '1.0.0',
                    version: version ?? '1.0.0',
                },
                releaseId: currentReleaseId ?? '1.0.0',
                status: 'completed',
                triggeredAt: generateRandomTimelineTimestamp(4, 13),
                triggeredBy: 'runway',
                type: 'release.submitted',
                userId: null,
                releaseStepArchetypes: [ReleaseStepType.Submission],
            },
            {
                appId: appId ?? 'demo',
                eventData: null,
                orgId: orgId ?? 'demo',
                releaseData: {
                    displayableVersion: version ?? '1.0.0',
                    version: version ?? '1.0.0',
                },
                releaseId: currentReleaseId ?? '1.0.0',
                status: 'completed',
                triggeredAt: generateRandomTimelineTimestamp(4, 21),
                triggeredBy: 'unknown',
                type: 'release.stepsReadyToSubmit',
                userId: null,
                releaseStepArchetypes: [ReleaseStepType.Submission],
            },
        ] as TimelineEvent[];

        baseTimelineEvents.splice(0, 0, ...submitAndReleaseTimelineEvents);

        if (platform === AppPlatform.Ios) {
            const appStoreReviewTimelineEvents = [
                {
                    appData: {
                        appName: appName ?? 'Demo',
                        platform: platform ?? 'ios',
                    },
                    appId: appId ?? 'demo',
                    eventData: {
                        releaseStepStatus: 'ready',
                        releaseStepType: 'takeoff',
                    },
                    orgId: orgId ?? 'demo',
                    releaseData: {
                        version: version ?? '1.0.0',
                    },
                    releaseId: currentReleaseId ?? '1.0.0',
                    status: 'completed',
                    triggeredAt: generateRandomTimelineTimestamp(4, 0),
                    triggeredBy: 'unknown',
                    type: 'releaseStep.statusChanged',
                    userId: null,
                    releaseStepArchetypes: [ReleaseStepType.Takeoff],
                },
                {
                    appData: {
                        appName: appName ?? 'Demo',
                        platform: platform ?? 'ios',
                    },
                    appId: appId ?? 'demo',
                    eventData: {
                        appStoreBuildIntegrationType: appStoreIntegration,
                        appStoreBuildNumber: appStoreBuildNumber,
                        appStoreStatus: 'READY_FOR_SALE',
                        betaServiceName: 'TestFlight',
                        multipleBuildData: [
                            {
                                appStoreBuildNumber: appStoreBuildNumber,
                                ciBuildNumber: ciBuildNumber,
                            },
                        ],
                    },
                    orgId: orgId ?? 'demo',
                    releaseData: {
                        version: version ?? '1.0.0',
                    },
                    releaseId: currentReleaseId ?? '1.0.0',
                    status: 'completed',
                    triggeredAt: generateRandomTimelineTimestamp(4, 1),
                    triggeredBy: 'unknown',
                    type: 'appStoreVersion.statusUpdated',
                    userId: null,
                    releaseStepArchetypes: [ReleaseStepType.Takeoff],
                },
                {
                    appData: {
                        appName: appName ?? 'Demo',
                        platform: platform ?? 'ios',
                    },
                    appId: appId ?? 'demo',
                    eventData: null,
                    orgId: orgId ?? 'demo',
                    releaseData: {
                        version: version ?? '1.0.0',
                    },
                    releaseId: currentReleaseId ?? '1.0.0',
                    status: 'completed',
                    triggeredAt: generateRandomTimelineTimestamp(4, 1),
                    triggeredBy: 'unknown',
                    type: 'release.stepsReadyToRelease',
                    userId: null,
                    releaseStepArchetypes: [ReleaseStepType.Takeoff],
                },
                {
                    appData: {
                        appName: appName ?? 'Demo',
                        platform: platform ?? 'ios',
                    },
                    appId: appId ?? 'demo',
                    eventData: {
                        appStoreBuildIntegrationType: appStoreIntegration,
                        appStoreBuildNumber: appStoreBuildNumber,
                        appStoreStatus: 'PENDING_DEVELOPER_RELEASE',
                        betaServiceName: 'TestFlight',
                        multipleBuildData: [
                            {
                                appStoreBuildNumber: appStoreBuildNumber,
                                ciBuildNumber: ciBuildNumber,
                            },
                        ],
                    },
                    orgId: orgId ?? 'demo',
                    releaseData: {
                        version: version ?? '1.0.0',
                    },
                    releaseId: currentReleaseId ?? '1.0.0',
                    status: 'completed',
                    triggeredAt: generateRandomTimelineTimestamp(4, 1),
                    triggeredBy: 'unknown',
                    type: 'appStoreVersion.statusUpdated',
                    userId: null,
                    releaseStepArchetypes: [ReleaseStepType.StoreReview],
                },
                {
                    appData: {
                        appName: appName ?? 'Demo',
                        platform: platform ?? 'ios',
                    },
                    appId: appId ?? 'demo',
                    eventData: {
                        appStoreBuildIntegrationType: appStoreIntegration,
                        appStoreBuildNumber: appStoreBuildNumber,
                        appStoreStatus: 'IN_REVIEW',
                        betaServiceName: 'TestFlight',
                        multipleBuildData: [
                            {
                                appStoreBuildNumber: appStoreBuildNumber,
                                ciBuildNumber: ciBuildNumber,
                            },
                        ],
                    },
                    orgId: orgId ?? 'demo',
                    releaseData: {
                        version: version ?? '1.0.0',
                    },
                    releaseId: currentReleaseId ?? '1.0.0',
                    status: 'completed',
                    triggeredAt: generateRandomTimelineTimestamp(4, 1),
                    triggeredBy: 'unknown',
                    type: 'appStoreVersion.statusUpdated',
                    userId: null,
                    releaseStepArchetypes: [ReleaseStepType.StoreReview],
                },
                {
                    appData: {
                        appName: appName ?? 'Demo',
                        platform: platform ?? 'ios',
                    },
                    appId: appId ?? 'demo',
                    eventData: {
                        releaseStepStatus: 'pending',
                        releaseStepType: 'storeReview',
                    },
                    orgId: orgId ?? 'demo',
                    releaseData: {
                        version: version ?? '1.0.0',
                    },
                    releaseId: currentReleaseId ?? '1.0.0',
                    status: 'completed',
                    triggeredAt: generateRandomTimelineTimestamp(4, 1),
                    triggeredBy: 'unknown',
                    type: 'releaseStep.statusChanged',
                    userId: null,
                    releaseStepArchetypes: [ReleaseStepType.StoreReview],
                },
                {
                    appId: appId ?? 'demo',
                    eventData: {
                        appStoreBuildIntegrationType: appStoreIntegration,
                        appStoreBuildNumber: appStoreBuildNumber,
                        appStoreStatus: 'WAITING_FOR_REVIEW',
                        betaServiceName: 'TestFlight',
                        multipleBuildData: [
                            {
                                appStoreBuildNumber: appStoreBuildNumber,
                                ciBuildNumber: ciBuildNumber,
                            },
                        ],
                    },
                    orgId: orgId ?? 'demo',
                    releaseData: {
                        version: version ?? '1.0.0',
                    },
                    releaseId: currentReleaseId ?? '1.0.0',
                    status: 'completed',
                    triggeredAt: generateRandomTimelineTimestamp(4, 1),
                    triggeredBy: 'unknown',
                    type: 'appStoreVersion.statusUpdated',
                    userId: null,
                    releaseStepArchetypes: [ReleaseStepType.Submission],
                },
            ] as TimelineEvent[];
            baseTimelineEvents.splice(3, 0, ...appStoreReviewTimelineEvents);
        }
    }

    return baseTimelineEvents.filter((event) => !disallowedTimelineEventsForPlatform.includes(event.type));
};

export const generateStepStatusUpdatedEvent = (
    appId: string,
    stepRunId: string,
    releaseId: string,
    version: string,
    releaseStepType: ReleaseStepType,
    releaseStepStatus: ReleaseStepStatus,
): TimelineEvent => {
    let daysAgo: number;

    // Determine days ago based on step type to keep certain order
    switch (releaseStepType) {
        case ReleaseStepType.Kickoff:
        case ReleaseStepType.FeatureReadiness:
            daysAgo = 3;
            break;
        case ReleaseStepType.ReleaseCandidate:
        case ReleaseStepType.BetaTesting:
        case ReleaseStepType.RegressionTesting:
            daysAgo = 2;
            break;
        case ReleaseStepType.Approvals:
        case ReleaseStepType.Metadata:
        case ReleaseStepType.Screenshots:
            daysAgo = 1;
            break;
        case ReleaseStepType.Submission:
        case ReleaseStepType.StoreReview:
        case ReleaseStepType.Takeoff:
        case ReleaseStepType.Rollout:
            daysAgo = 0;
            break;
        default:
            // Default fallback for any other step types
            daysAgo = 1;
    }

    return {
        type: 'releaseStep.statusChanged',
        triggeredAt: generateRandomTimelineTimestamp(daysAgo),
        triggeredBy: 'unknown',
        userId: null,
        status: 'completed',
        eventData: { releaseStepType, releaseStepStatus },
        orgId: 'demo',
        appId: appId,
        releaseId,
        releaseData: {
            version: version ?? '1.0.0',
        },
        releaseStepArchetypes: [releaseStepType],
        previouslySeenAt: [],
        destinationStepConfigIds: [],
        stepRunId,
    } as unknown as TimelineEvent;
};

export const generateTimelineEventForBatchSubmit = ({
    releaseId,
    version,
    workflowRunId,
    appId,
    stepRunGraph,
    workflowStepGraph,
}: {
    releaseId: string;
    version: string;
    workflowRunId: string;
    appId: string;
    stepRunGraph: WorkflowRunStepRunGraph;
    workflowStepGraph: { stepGroups: any[] };
}): TimelineEvent => {
    const batchTransactionId = `batchtrx-${Math.random().toString(36).substring(2, 15)}`;
    const now = new Date().toISOString();
    const requestedAt = new Date(Date.now() - 30000).toISOString();

    const submissionSteps = collectStepsByArchetypeFromGraph(stepRunGraph, ReleaseStepType.Submission);
    const stepConfigIds = submissionSteps.map((s) => s.stepConfigId);
    const stepRunIds = submissionSteps.map((s) => s.stepRunId);
    const stepDisplayNames = submissionSteps.map((s) => s.stepName);

    const stepConfigToDestinations = buildStepConfigToDestinationsMap(workflowStepGraph);
    const destinationStepConfigIds: string[] = [];
    stepConfigIds.forEach((id) => {
        const destIds = stepConfigToDestinations[id] || [];
        destinationStepConfigIds.push(...destIds);
    });

    const eventData: TimelineEventBatchActionRequestedData = {
        batchTransactionId,
        failedForStepRunIds: [],
        failuresDestStepConfigIds: [],
        lastAttemptedAt: now,
        maxAttempts: 3,
        pendingRetryDestStepConfigIds: [],
        pendingRetryForStepRunIds: [],
        requestedAt,
        targetStepRunIds: stepRunIds,
        succeededForStepRunIds: stepRunIds,
        targetDestStepConfigIds: destinationStepConfigIds,
        successesDestStepConfigIds: destinationStepConfigIds,
        transactionType: TransactionType.SubmitVersion,
        targetStepDisplayNames: stepDisplayNames,
        successesStepDisplayNames: stepDisplayNames,
        pendingRetryStepDisplayNames: [],
        failuresStepDisplayNames: [],
        stepGroupName: 'Submission',
    };

    return {
        appId,
        releaseId,
        orgId: 'demo',
        userId: 'ssullenberger@demo.com',
        title: 'Batch submit for review action successful',
        status: 'completed',
        releaseData: { displayableVersion: version, version: version },
        eventData,
        triggeredAt: generateRandomTimelineTimestamp(17, 11),
        triggeredBy: 'runway-admin',
        type: TimelineEventType.BatchActionRequested,
        releaseStepArchetypes: [ReleaseStepType.Submission],
        stepConfigIds,
        automationData: null,
        additionalData: {},
        workflowRunId,
        stepRunId: null,
        destinationStepConfigIds,
        previouslySeenWithErrorAt: [],
        previouslySeenCount: 0,
    } as TimelineEvent;
};

// Add random time variation within the day (0-24 hours)
const generateRandomTimelineTimestamp = (daysAgo = 0, hoursAgo = 0) => {
    const oneDayMs = 24 * 60 * 60 * 1000;
    const oneHourMs = 60 * 60 * 1000;
    const randomTimeOffset = Math.random() * oneDayMs;
    return new Date(Date.now() - (daysAgo * oneDayMs + hoursAgo * oneHourMs + randomTimeOffset)).toISOString();
};
